import { DateTime } from 'luxon';
import { ResponsiveBar } from '@nivo/bar';
import styled from 'styled-components';
import ChartContainer from './ChartContainer';
import { ORANGE_500 } from '../../constants';
import {
    CardDescription,
    CardTitle,
    TotalUsage,
    UsageCard,
    UsageCardHeader,
    UsageSummaryRow,
} from './styled';
import BarTooltip from './BarTooltip';
import SeeDetailsDropdown from './SeeDetailsDropdown';
import PricingTiersTable from './SeeDetailsDropdown/PricingTiersTable';
import getFlatTierLimit from './helpers/getFlatTierLimit';
import { ResourceUsage } from './types';

const ChartWrapper = styled.div`
    width: 100%;
    height: 300px;
`;

interface UsageCardItemProps {
    resource: ResourceUsage;
    isLoading?: boolean;
    periodStart: string;
    periodEnd: string;
}

const UsageCardItem = ({
    resource,
    isLoading,
    periodStart,
    periodEnd,
}: UsageCardItemProps) => {
    const usageMap = new Map(
        resource.usage.map((item) => [
            DateTime.fromISO(item.date).toISODate(),
            item.quantity,
        ]),
    );
    const start = DateTime.fromISO(periodStart);
    const end = DateTime.fromISO(periodEnd);
    const diffInDays = end.diff(start, 'days').days;

    const chartData = Array.from({ length: Math.ceil(diffInDays) + 1 }, (_, index) => {
        const currentDate = start.plus({ days: index });
        const isoDate = currentDate.toISODate();
        return {
            date: currentDate.toFormat('MMM d'),
            quantity: usageMap.get(isoDate) || 0,
            unit: resource.unit || 'unit',
        };
    });

    const totalUsage = chartData.reduce((acc, curr) => acc + curr.quantity, 0);
    const unitToUse = resource.unit || 'unit';
    const plural = new Intl.PluralRules('en-US', { type: 'cardinal' }).select(totalUsage);
    const totalUsageUnit = plural === 'one'
        ? unitToUse.slice(0, -1)
        : unitToUse;
    const flatLimit = getFlatTierLimit(resource.tiers, resource.unit);
    const showDetails = Boolean(
        resource.tiers?.length && resource.currency,
    );

    return (
        <UsageCard>
            <UsageCardHeader>
                <CardTitle>
                    {resource.name}
                </CardTitle>
                <CardDescription>
                    {resource.description}
                </CardDescription>
                <UsageSummaryRow>
                    <TotalUsage>
                        {totalUsage}
                        {flatLimit != null && (
                            <>
                                {' / '}
                                {flatLimit}
                            </>
                        )}
                        {' '}
                        {totalUsageUnit}
                    </TotalUsage>
                    {showDetails && (
                        <SeeDetailsDropdown>
                            <PricingTiersTable
                                tiers={resource.tiers as NonNullable<ResourceUsage['tiers']>}
                                currency={resource.currency as string}
                                unit={resource.unit}
                                totalUsage={totalUsage}
                            />
                        </SeeDetailsDropdown>
                    )}
                </UsageSummaryRow>
            </UsageCardHeader>
            <ChartContainer isLoading={isLoading}>
                <ChartWrapper id="chartContainer">
                    <ResponsiveBar
                        data={chartData}
                        keys={['quantity']}
                        indexBy="date"
                        indexScale={{
                            type: 'band',
                        }}
                        margin={{
                            top: 10,
                            right: 20,
                            bottom: 50,
                            left: 40,
                        }}
                        padding={0.3}
                        colors={ORANGE_500}
                        borderRadius={4}
                        axisBottom={{
                            tickRotation: -45,
                        }}
                        axisLeft={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: 0,
                            legend: resource.unit || 'unit',
                            legendPosition: 'middle',
                            legendOffset: -35,
                            format: (e) => (Number.isInteger(e) ? e : ''),
                        }}
                        tooltip={BarTooltip}
                        animate
                    />
                </ChartWrapper>
            </ChartContainer>
        </UsageCard>
    );
};

interface Props {
    resources?: ResourceUsage[];
    isLoading?: boolean;
    periodStart: string;
    periodEnd: string;
}

const UsageCards = ({
    resources,
    isLoading,
    periodStart,
    periodEnd,
}: Props) => resources?.map((resource) => (
    <UsageCardItem
        key={resource.name}
        resource={resource}
        isLoading={isLoading}
        periodStart={periodStart}
        periodEnd={periodEnd}
    />
));

export default UsageCards;
